import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, TIMEFRAMES } from "@/lib/api";
import ProCandleChart from "@/components/ProCandleChart";
import TrendPanel from "@/components/TrendPanel";
import SignalBox from "@/components/SignalBox";
import InstitutionalFilters, { loadToggles } from "@/components/InstitutionalFilters";
import EventsPanel from "@/components/EventsPanel";
import RegimePanel from "@/components/RegimePanel";
import ForecastPanel from "@/components/ForecastPanel";
import TermTooltip from "@/components/TermTooltip";
import SheetTabs from "@/components/SheetTabs";
import FloatingWatchlist from "@/components/FloatingWatchlist";
import { TesterChecklist } from "@/components/TesterChecklist";
import DataHealthBadge from "@/components/DataHealthBadge";
import OnboardingWizard, { useOnboarding } from "@/components/OnboardingWizard";
import { useApp } from "@/context/AppContext";
import { RefreshCw, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const POPULAR = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD", "ETHUSD", "NAS100", "SPX500"];

// Sheet persistence
const SHEETS_LS_KEY = "qtr-sheets";
const ACTIVE_SHEET_LS_KEY = "qtr-active-sheet";

function loadSheets() {
  try {
    const raw = localStorage.getItem(SHEETS_LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch { /* noop */ }
  return [{ id: "s1", symbol: "EURUSD", timeframe: "H1", name: null }];
}

function saveSheets(sheets) {
  try { localStorage.setItem(SHEETS_LS_KEY, JSON.stringify(sheets)); } catch { /* noop */ }
}

export default function Dashboard() {
  const { t } = useApp();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState({});

  // Multi-sheet workspace state
  const [sheets, setSheets] = useState(loadSheets);
  const [activeSheetId, setActiveSheetId] = useState(() => {
    try { return localStorage.getItem(ACTIVE_SHEET_LS_KEY) || loadSheets()[0].id; } catch { return "s1"; }
  });

  const activeSheet = sheets.find((s) => s.id === activeSheetId) || sheets[0];
  const symbol = activeSheet?.symbol || "EURUSD";
  const timeframe = activeSheet?.timeframe || "H1";

  // Update active sheet's fields
  const patchSheet = useCallback((id, patch) => {
    setSheets((prev) => {
      const next = prev.map((s) => (s.id === id ? { ...s, ...patch } : s));
      saveSheets(next);
      return next;
    });
  }, []);
  const setSymbol = (sym) => patchSheet(activeSheetId, { symbol: sym });
  const setTimeframe = (tf) => patchSheet(activeSheetId, { timeframe: tf });

  const addSheet = () => {
    const nid = `s${Date.now()}`;
    const nextSheet = { id: nid, symbol: "EURUSD", timeframe: "H1", name: null };
    const next = [...sheets, nextSheet];
    setSheets(next); saveSheets(next);
    setActiveSheetId(nid);
    try { localStorage.setItem(ACTIVE_SHEET_LS_KEY, nid); } catch { /* noop */ }
  };
  const closeSheet = (id) => {
    if (sheets.length <= 1) return;
    const next = sheets.filter((s) => s.id !== id);
    setSheets(next); saveSheets(next);
    if (id === activeSheetId) {
      const first = next[0].id;
      setActiveSheetId(first);
      try { localStorage.setItem(ACTIVE_SHEET_LS_KEY, first); } catch { /* noop */ }
    }
  };
  const renameSheet = (id, name) => patchSheet(id, { name });
  const selectSheet = (id) => {
    setActiveSheetId(id);
    try { localStorage.setItem(ACTIVE_SHEET_LS_KEY, id); } catch { /* noop */ }
  };

  // Open a symbol from the floating watchlist:
  // - if a sheet already tracks that symbol on the current TF, just activate it
  // - otherwise, spawn a new sheet, activate it, and load the symbol
  const openSymbolInNewSheet = (sym) => {
    const existing = sheets.find((s) => s.symbol === sym);
    if (existing) {
      setActiveSheetId(existing.id);
      try { localStorage.setItem(ACTIVE_SHEET_LS_KEY, existing.id); } catch { /* noop */ }
      return;
    }
    const nid = `s${Date.now()}`;
    const nextSheet = { id: nid, symbol: sym, timeframe: timeframe || "H1", name: null };
    const next = [...sheets, nextSheet];
    setSheets(next); saveSheets(next);
    setActiveSheetId(nid);
    try { localStorage.setItem(ACTIVE_SHEET_LS_KEY, nid); } catch { /* noop */ }
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [instScore, setInstScore] = useState(null);
  // First-visit onboarding wizard (localStorage-gated)
  const onboarding = useOnboarding();

  // Multi-pane grid state (1/2/4/6/8)
  const [paneCount, setPaneCount] = useState(1);
  const [paneTfs, setPaneTfs] = useState(["H4", "H1", "M15", "M5", "M1", "D1", "W1"]);
  const [paneData, setPaneData] = useState([null, null, null, null, null, null, null]);
  const [paneLoading, setPaneLoading] = useState([false, false, false, false, false, false, false]);

  const setPaneTf = (idx, tf) => setPaneTfs((prev) => prev.map((v, i) => (i === idx ? tf : v)));
  const availableSplitTfs = useMemo(() => TIMEFRAMES.filter((tf) => tf !== timeframe), [timeframe]);

  useEffect(() => {
    api.symbols().then((d) => setCatalog(d.catalog || {})).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.analyze(symbol, timeframe);
      setData(res);
    } catch (e) {
      toast.error("Erreur lors de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [symbol, timeframe]);

  // Fetch data for each additional pane whenever its TF or symbol changes
  useEffect(() => {
    // Panes 1..N-1 are extra (pane 0 is main data). Fetch/refetch on symbol or tf change.
    for (let i = 1; i < paneCount; i++) {
      const tf = paneTfs[i - 1];
      let cancelled = false;
      setPaneLoading((prev) => prev.map((v, idx) => (idx === i - 1 ? true : v)));
      api.analyze(symbol, tf)
        .then((res) => { if (!cancelled) setPaneData((prev) => prev.map((v, idx) => (idx === i - 1 ? res : v))); })
        .catch(() => { if (!cancelled) toast.error(`Erreur chargement ${tf}`); })
        .finally(() => { if (!cancelled) setPaneLoading((prev) => prev.map((v, idx) => (idx === i - 1 ? false : v))); });
      // Note: cleanup not chained across iterations, safe because paneCount is small
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paneCount, symbol, JSON.stringify(paneTfs.slice(0, Math.max(0, paneCount - 1)))]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [autoRefresh, symbol, timeframe]);

  const notify = async () => {
    setNotifying(true);
    try {
      const toggles = loadToggles();
      const filters = Object.keys(toggles).filter((k) => toggles[k]);
      const res = await api.analyzeAndNotify(symbol, timeframe, true, filters);
      setData(res);
      if (res.alert_suppressed) {
        toast.warning(res.alert_suppressed_reason || "Alerte non envoyée — confluence institutionnelle insuffisante");
      } else {
        toast.success("Alerte envoyée sur vos canaux configurés");
      }
    } catch (e) {
      toast.error("Impossible d'envoyer l'alerte. Vérifiez vos paramètres.");
    } finally {
      setNotifying(false);
    }
  };

  const lastPrice = data?.candles?.[data.candles.length - 1]?.close;

  return (
    <div className="p-6 space-y-4">
      {/* Excel-style sheet tabs — one workspace per asset */}
      <SheetTabs
        sheets={sheets}
        activeId={activeSheetId}
        onSelect={selectSheet}
        onAdd={addSheet}
        onClose={closeSheet}
        onRename={renameSheet}
      />

      <TesterChecklist />
      {user?.is_beta_tester && (
        <div className="rounded-xl border border-cyan-500/40 bg-cyan-500/10 p-4 flex items-center justify-between gap-4" data-testid="beta-access-banner">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cyan-300">Statut</p>
            <h3 className="mt-0.5 font-heading text-base font-semibold text-cyan-100">Accès bêta complet — aucune rétrogradation pendant la beta</h3>
          </div>
          <Link to="/app/beta-status" className="rounded-full border border-cyan-500/30 bg-slate-900/80 px-3 py-1 text-[10px] font-bold tracking-widest text-cyan-200 hover:bg-cyan-500/20 shrink-0" data-testid="beta-banner-link">
            BETA TESTEUR →
          </Link>
        </div>
      )}

      {/* Topbar */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight" data-testid="dashboard-title">{t("dashboard.title", "Tableau de Bord")}</h1>
          <p className="text-white/50 text-sm mt-1">{t("dashboard.subtitle", "Analyse propriétaire multi-marchés — passé, présent, futur.")}</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">{t("dashboard.asset", "Actif")}</label>
            <select
              className="field w-52"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              data-testid="symbol-selector"
            >
              {Object.entries(catalog).map(([cat, list]) => (
                <optgroup key={cat} label={cat}>
                  {list.map((s) => <option key={s} value={s}>{s}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="label">{t("dashboard.timeframe", "Unité de temps")}</label>
            <select
              className="field w-28"
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              data-testid="timeframe-selector"
            >
              {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </div>
          <button className="btn" onClick={load} disabled={loading} data-testid="refresh-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {t("common.refresh", "Actualiser")}
          </button>
          <label className="chip cursor-pointer" data-testid="auto-refresh-toggle">
            <input type="checkbox" className="accent-blue-500" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            {t("dashboard.auto_refresh", "Auto 30s")}
          </label>
          <DataHealthBadge />
        </div>
      </div>

      {/* Data source notice */}
      {data?.source === "demo" && (
        <div className="panel p-3 flex items-start gap-3 text-xs text-white/70" data-testid="demo-notice">
          <AlertCircle size={14} className="text-warn mt-0.5 shrink-0" />
          <div>
            <b>Mode démo actif.</b> Les prix affichés sont synthétisés pour permettre l&apos;analyse. Ajoutez votre clé API <code className="font-mono">TWELVE_DATA_API_KEY</code> dans les paramètres serveur pour brancher les données de marché en temps réel.
            {data?.source_reason && <span className="text-white/40"> ({data.source_reason})</span>}
          </div>
        </div>
      )}
      {data && data.provider_covered === false && (
        <div className="panel p-3 flex items-start gap-3 text-xs text-white/70 border-warn/40" data-testid="uncovered-notice">
          <AlertCircle size={14} className="text-warn mt-0.5 shrink-0" />
          <div>
            <b>Symbole non couvert</b> par notre fournisseur principal (Twelve Data) — les prix sont générés en mode démo même si votre clé est configurée. Pour un vrai flux temps-réel sur {symbol}, il faudrait brancher un provider local/régional.
          </div>
        </div>
      )}

      {/* Price header */}
      <div className="flex flex-wrap items-baseline gap-4">
        <div className="font-heading font-bold text-3xl" data-testid="current-symbol">{symbol}</div>
        <div className="font-mono text-3xl tracking-tight" data-testid="current-price">
          {lastPrice != null ? lastPrice.toFixed(5) : "—"}
        </div>
        <div className="text-white/50 text-sm">{timeframe}</div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-8">
          <div className="panel p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="panel-title text-[14px]">Graphique en bougies</div>
              <div className="flex items-center gap-3 text-[11px] text-white/50">
                <LegendDot color="rgba(0,122,255,0.6)" label={<TermTooltip term="ZII">ZII haussière</TermTooltip>} />
                <LegendDot color="rgba(255,59,48,0.6)" label={<TermTooltip term="ZII">ZII baissière</TermTooltip>} />
                <LegendDot color="rgba(245,166,35,0.7)" label={<TermTooltip term="DP">DP</TermTooltip>} />
                <LegendDot color="rgba(139,92,246,0.8)" label={<TermTooltip term="RL">RL</TermTooltip>} />
              </div>
            </div>
            {loading && !data ? (
              <div className="h-[460px] grid place-items-center text-white/40">
                <Loader2 className="animate-spin" />
              </div>
            ) : (
              <ProCandleChart
                candles={data?.candles}
                zones={data?.zones}
                structure={data?.structure}
                symbol={symbol}
                timeframe={timeframe}
                onTimeframeChange={setTimeframe}
                allTimeframes={TIMEFRAMES}
                paneCount={paneCount}
                onPaneCountChange={setPaneCount}
                paneTfs={paneTfs}
                setPaneTf={setPaneTf}
                paneData={paneData}
                paneLoading={paneLoading}
                availableSplitTfs={availableSplitTfs}
              />
            )}
          </div>
        </div>
        <div className="col-span-12 xl:col-span-4 space-y-4">
          <RegimePanel regime={data?.regime} htf={data?.htf_bias} />
          <TrendPanel trends={data?.trends} />
          <InstitutionalFilters institutional={data?.institutional} onScoreChange={setInstScore} />
          <ForecastPanel forecast={data?.forecast} />
          <SignalBox signal={data?.signal} onNotify={notify} notifying={notifying} instScore={instScore} />
        </div>
        <div className="col-span-12">
          <EventsPanel events={data?.events} />
        </div>
      </div>

      {/* Floating watchlist — bottom-right, 10-20 favorite assets, click → new sheet */}
      <FloatingWatchlist onOpenSymbol={openSymbolInNewSheet} catalog={catalog} />

      {/* First-visit onboarding — 3-step wizard, LS-gated so it never re-shows */}
      {onboarding.show && <OnboardingWizard onClose={onboarding.close} />}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: color }} /> {label}
    </span>
  );
}
